import { Injectable } from '@angular/core';
import { App, AlertController, ModalController, NavController, ToastController } from 'ionic-angular';
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { ApiService } from "./api.service";
import { SearchService } from "./search.service";
import Raven from 'raven-js';
import { Storage } from '@ionic/storage';
import { IFilter } from '../interfaces/Filter';
import { AuthService } from "./auth.service";
import { AlertService } from "./alert.service";

export { IFilter }

@Injectable()
export class FilterService {
  private navCtrl: NavController;

  public filter: IFilter;
  private url: string;

  public filterSubject: BehaviorSubject<IFilter> = new BehaviorSubject<any>(null);

  constructor(
    private _apiService: ApiService,
    private storage: Storage,
    private _searchService: SearchService,
    private toastCtrl: ToastController,
    private modalCtrl: ModalController,
    private _authService: AuthService,
    private _alertService: AlertService,
    private app: App) {
    this.storage.get('filter').then(res => {
      this.navCtrl = this.app.getActiveNav();

      if (res) {
        this.setFilter(res, false);
      } else {
        this.setFilter(this.getDefaultFilter());
      }
    });
  }

  setFilter(filter, persist = true) {
    this.filter = filter;
    if (persist) {
      this.saveFilter();
    }
    this.broadcastFilter();
    this.generateRequestUrl();
  }

  saveFilter() {
    this.storage.set('filter', this.filter);
  }

  toggleHousingFilter() {
    this.filter.housing.active = !this.filter.housing.active;
    this.generateRequestUrl();
  }

  toggleJobFilter() {
    this.filter.job.active = !this.filter.job.active;
    this.generateRequestUrl();
  }

  toggleSaleFilter() {
    this.filter.sale.active = !this.filter.sale.active;
    this.generateRequestUrl();
  }

  toggleHelpFilter() {
    this._authService.getUserInfo().then(res => {
      if (res.is_student) {
        this.filter.help.active = !this.filter.help.active;
        this.generateRequestUrl();
      } else {
        this._alertService.presentNotStudentAlert();
      }
    }).catch(err => {
      this._alertService.presentNotStudentAlert();
    });
  }

  broadcastFilter() {
    this.filterSubject.next(this.filter);
  }

  getFilterTypes(typeObject) {
    if (!typeObject) return;

    if (typeObject && typeObject.length === 0) {
      return 'Alle';
    } else if (typeObject && typeObject.length === 1) {
      return typeObject[0].name;
    } else {
      return 'Mehrere';
    }
  }

  getFilterPrice(priceObject) {
    if (!priceObject) return;

    if (priceObject.low === 0 && priceObject.high === 0) {
      return 'Alle';
    } else {
      return priceObject.low + ' - ' + priceObject.high;
    }
  }

  getFilterLocations(locationObject) {
    if (!locationObject) return;

    if (locationObject && !locationObject.name) {
      return 'Überall';
    } else {
      return locationObject.name;
    }
  }

  showFilterModal(type, filterType, data) {
    let dataClone = !!data ? JSON.parse(JSON.stringify(data)) : {};
    let modal = this.modalCtrl.create('filter-modal', {
      type: type,
      filterType: filterType,
      data: dataClone
    });
    modal.onDidDismiss(data => {
      if (data) {
        if (data.latitude && data.longitude && data.name) {
          if (type === 'housing') {
            this.filter.housing.location = {
              name: data.name,
              lat: data.latitude,
              long: data.longitude
            };
          } else {
            this.filter.job.location = {
              name: data.name,
              lat: data.latitude,
              long: data.longitude
            };
          }
        }

        if (filterType === 'branche') {
          if (type === 'job') {
            this.filter.job.branches = data;
          }
        }

        if (filterType === 'types' || filterType === 'category') {
          if (type === 'help') {
            this.filter.help.categories = data;
          } else if (type === 'housing') {
            this.filter.housing.types = data;
          } else if (type === 'sale') {
            this.filter.sale.categories = data;
          } else if (type === 'job') {
            this.filter.job.categories = data;
          }
        }

        if (data.low !== undefined && data.high !== undefined) {
          this.filter.housing.price.low = +data.low;
          this.filter.housing.price.high = +data.high;
        }
      } else {
        if (filterType === 'location') {
          if (type === 'housing') {
            this.filter.housing.location = { name: '', lat: 0, long: 0 };
          } else {
            this.filter.job.location = { name: '', lat: 0, long: 0 };
          }
        }
      }
      this.generateRequestUrl();
    });
    modal.present();
  }

  generateRequestUrl() {
    this.saveFilter();

    let urlPieces = [];
    if (this.filter.housing.active) {
      if (!(this.filter.housing.price.low === 0 && this.filter.housing.price.high === 0)) {
        urlPieces.push('housing_rent_low=' + this.filter.housing.price.low);
        urlPieces.push('housing_rent_high=' + this.filter.housing.price.high);
      }

      if (this.filter.housing.types.length === 0) {
        urlPieces.push('housing_category=ALL');
      } else {
        urlPieces.push('housing_category=' + this.filter.housing.types.map(e => e.id).join(','));
      }

      if (this.filter.housing.location && this.filter.housing.location.name) {
        urlPieces.push('housing_lat=' + this.filter.housing.location.lat);
        urlPieces.push('housing_long=' + this.filter.housing.location.long);
        urlPieces.push('housing_range=5');
      }
    }

    if (this.filter.job.active) {
      if (this.filter.job.categories.length === 0) {
        urlPieces.push('job_category=ALL');
      } else {
        urlPieces.push('job_category=' + this.filter.job.categories.map(e => e.id).join(','));
      }

      if (this.filter.job.branches.length === 0) {
        urlPieces.push('job_branche=ALL');
      } else {
        urlPieces.push('job_branche=' + this.filter.job.branches.map(e => e.id).join(','));
      }

      if (this.filter.job.location && this.filter.job.location.name) {
        urlPieces.push('job_lat=' + this.filter.job.location.lat);
        urlPieces.push('job_long=' + this.filter.job.location.long);
        urlPieces.push('job_range=5');
      }
    }

    if (this.filter.sale.active) {
      if (this.filter.sale.categories.length === 0) {
        urlPieces.push('sale_category=ALL');
      } else {
        urlPieces.push('sale_category=' + this.filter.sale.categories.map(e => e.id).join(','));
      }
    }

    if (this.filter.help.active) {
      if (this.filter.help.categories.length === 0) {
        urlPieces.push('help_category=ALL');
      } else {
        urlPieces.push('help_category=' + this.filter.help.categories.map(e => e.id).join(','));
      }
    }

    this.url = urlPieces.join('&');
    this._searchService.setParams(this.url);
  }

  saveFilterToServer() {
    let filter = {
      "fulltext": this._searchService.getSearchTerm()
    };

    let parts = this.url.split('&');
    parts.forEach(p => {
      if (p) {
        let pair = p.split('=');
        filter[pair[0]] = pair[1];
      }
    })

    return new Promise((resolve, reject) => {
      this._apiService.saveFilter(filter).then(res => {
        this.toastCtrl.create({
          message: 'Suchabo wurde gespeichert',
          duration: 2000,
          showCloseButton: true
        }).present();
        resolve();
      }).catch(err => {
        this.toastCtrl.create({
          message: 'Suchabo konnte nicht gespeichert werden. Bitte versuche es erneut.',
          duration: 2000,
          showCloseButton: true
        }).present();
        reject(err);
      });
    });
  }

  getDefaultFilter() {
    return {
      housing: {
        active: true,
        location: { name: '', lat: 0, long: 0 },
        types: [],
        price: { low: 0, high: 0 }
      },
      job: {
        active: true,
        location: { name: '', lat: 0, long: 0 },
        categories: [],
        branches: [],
      },
      sale: {
        active: true,
        categories: [],
      },
      help: {
        active: false,
        categories: [],
      }
    };
  }
}



// WEBPACK FOOTER //
// ./src/providers/filter.service.ts